import React from 'react';
import EmptyState from './EmptyState';
import Button from './Button';
import PageBackLink from './PageBackLink';

export type AdminQueueStateVariant = 'error' | 'empty' | 'filter-empty';

export interface AdminQueueStateProps {
  variant: AdminQueueStateVariant;
  title: string;
  description?: string;
  onRetry?: () => void;
  onClearFilter?: () => void;
  clearFilterLabel?: string;
  /** When true, show Back to Admin overview (defaults: true for error, false otherwise) */
  showAdminOverviewLink?: boolean;
  className?: string;
}

/**
 * Empty, filter-empty, and error states for admin list queues.
 * Errors offer retry + optional return to overview; empty queues stay in place.
 */
export const AdminQueueState: React.FC<AdminQueueStateProps> = ({
  variant,
  title,
  description,
  onRetry,
  onClearFilter,
  clearFilterLabel = 'Clear filter',
  showAdminOverviewLink,
  className = '',
}) => {
  const showBack = showAdminOverviewLink ?? variant === 'error';

  const hasAction = (variant === 'error' && onRetry) || (variant === 'filter-empty' && onClearFilter) || showBack;

  return (
    <EmptyState
      variant="dense"
      title={title}
      description={description}
      className={className}
      action={
        hasAction ? (
          <>
            {variant === 'error' && onRetry ? (
              <Button type="button" variant="primary" onClick={onRetry}>
                Try again
              </Button>
            ) : null}
            {variant === 'filter-empty' && onClearFilter ? (
              <Button type="button" variant="secondary" onClick={onClearFilter}>
                {clearFilterLabel}
              </Button>
            ) : null}
            {showBack ? <PageBackLink label="Back to Admin overview" to="/admin" /> : null}
          </>
        ) : undefined
      }
    />
  );
};

export default AdminQueueState;
